/**
 * The Settings page's tunables, as data.
 *
 * The port of `SettingsViewModel.BuildRows`/`BuildDto` and `SettingRow`. Forty-odd numbers that all
 * behave alike - a value, a unit, a default, sometimes an "unlimited" state - so they are a table
 * rather than forty hand-written fields, each one a place to bind a value to the wrong member.
 *
 *   * EVERY ROW NAMES ITS DTO MEMBER (`section` + `field`), and `buildSettings` writes each value back
 *     through that name onto a copy of what was loaded. A row added in the middle of a section cannot
 *     shift every value after it into the wrong field, and a member with no row keeps what is stored.
 *   * THE TEST PINS THE TABLE TO THE DTO: every numeric member of `AppSettingsDto` has exactly one row.
 *     A section appended server-side and forgotten here is the failure the C# comments warn about
 *     three times - it would compile, and quietly reset to the shipped defaults on every save.
 *   * `scale` is what lets a byte cap be edited in megabytes: nobody should type 209715200.
 *   * 0 STORED MEANS UNLIMITED where `allowUnlimited` is set, and the box keeps the last real number
 *     so unticking restores something sensible rather than an empty field.
 */
import type { AppSettingsDto } from './contracts.gen.js';

export type SectionKey = keyof AppSettingsDto;

export interface SettingRowSpec {
  section: SectionKey;
  field: string;
  label: string;
  help: string;
  unit: string;
  scale?: number;
  allowUnlimited?: boolean;
}

/** The Settings page tab a section of tunables is shown on. */
export type SettingsTabKey = 'general' | 'security' | 'mail' | 'payments' | 'devices' | 'mobile' | 'files' | 'updates';

export interface SettingSectionSpec {
  title: string;
  tab: SettingsTabKey;
  description: string;
  rows: SettingRowSpec[];
}

const KB = 1024;
const MB = 1024 * 1024;

function rows(section: SectionKey, list: Array<Omit<SettingRowSpec, 'section'>>): SettingRowSpec[] {
  return list.map(r => ({ section, ...r }));
}

export const SETTING_SECTIONS: readonly SettingSectionSpec[] = [
  {
    title: 'Sign-in security',
    tab: 'security',
    description:
      'Applies to staff sign-ins and customer-portal sign-ins alike. Failures are counted against the address that was typed, so an address that belongs to no account locks out the same way - which is what stops the message from telling an attacker who has an account here. An existing session is never signed out by a lockout, so nobody can be evicted from the CRM by someone else guessing at their password. Every refused sign-in is recorded whether or not lockouts are switched on.',
    rows: rows('signIn', [
      { field: 'maxFailedAttempts', label: 'Failed attempts before lockout', help: 'Consecutive failures inside the window below. Tick Unlimited to switch lockouts off entirely.', unit: 'attempts', allowUnlimited: true },
      { field: 'windowMinutes', label: 'Counting window', help: 'A failure arriving later than this starts the count again at one.', unit: 'minutes' },
      { field: 'lockoutMinutes', label: 'Lockout duration', help: 'How long the account refuses passwords. It expires on its own; an administrator can release it sooner from the Security page.', unit: 'minutes' },
      { field: 'logRetentionDays', label: 'Failed sign-in log retention', help: 'How long a recorded failed sign-in is kept.', unit: 'days' },
      { field: 'maxStoredFailures', label: 'Failed sign-ins kept', help: 'Ceiling on the whole log, oldest pruned first. It is written by anyone who can reach the sign-in form, so it needs a bound that does not depend on anyone behaving.', unit: 'entries' },
      { field: 'alertCooldownMinutes', label: 'Shortest gap between alert emails', help: 'The first lockout is emailed at once; anything inside this window is gathered into one summary at the end of it, so a password spray is not a flood of mail arriving exactly when it has to be read.', unit: 'minutes' }
    ])
  },
  {
    title: 'Audit log',
    tab: 'security',
    description:
      'Every action taken through the CRM - sign-ins, remote sessions, device commands, changes to customers, invoices and settings, and every refused request - is recorded on the Audit log page. Nothing removes a row; these two numbers are the only thing that does, and they are exactly what an insurer or a customer\'s security policy tends to specify.',
    rows: rows('audit', [
      { field: 'retentionDays', label: 'Audit log retention', help: 'How long a recorded action is kept.', unit: 'days' },
      { field: 'maxStoredEntries', label: 'Audit entries kept', help: 'Ceiling on the whole log, oldest pruned first. The bound that holds while something noisy is happening, when the age bound is doing nothing yet.', unit: 'entries' }
    ])
  },
  {
    title: 'Remote file transfers',
    tab: 'files',
    description:
      'Clipboard, drag-and-drop and the remote file explorer. Files are staged on the server and then moved, so a transfer occupies this much disk twice over. Removing the size limit removes this server\'s limit only - a reverse proxy in front of it keeps its own (nginx defaults to 1 MB).',
    rows: rows('remoteFiles', [
      { field: 'maxTotalBytes', label: 'Maximum transfer size', help: 'Total size of one transfer, in either direction.', unit: 'MB', scale: MB, allowUnlimited: true },
      { field: 'maxFiles', label: 'Maximum files per transfer', help: 'How many files one transfer may carry.', unit: 'files', allowUnlimited: true },
      { field: 'stagingTtlMinutes', label: 'Staged transfer lifetime', help: 'How long a staged transfer survives on the server before it is swept.', unit: 'minutes' }
    ])
  },
  {
    title: 'Remote file explorer',
    tab: 'files',
    description:
      'Raise the transfer timeout alongside the transfer size - a bigger cap without a longer timeout just moves the failure from a clean rejection to a stalled copy.',
    rows: rows('fileExplorer', [
      { field: 'maxEntries', label: 'Entries per folder listing', help: 'Larger folders are truncated and say so.', unit: 'rows' },
      { field: 'browseTimeoutSeconds', label: 'Listing timeout', help: 'How long the server waits for a listing or a rename/delete.', unit: 'seconds' },
      { field: 'transferTimeoutSeconds', label: 'Transfer timeout', help: 'How long the server waits for the device to move a file\'s contents.', unit: 'seconds' }
    ])
  },
  {
    title: 'Remote clipboard',
    tab: 'files',
    description:
      'Clipboard text shared during a remote-control session. Images are deliberately not shared - the live picture already shows them.',
    rows: rows('clipboard', [
      { field: 'maxChars', label: 'Maximum clipboard text', help: 'Longer text is truncated at whichever end copied it.', unit: 'KB', scale: KB }
    ])
  },
  {
    title: 'Event log viewer',
    tab: 'devices',
    description:
      'Reads a device\'s Windows logs live. Level, ID and time filters run inside the log itself; a free-text search has to read each record, which is what the scan limit bounds.',
    rows: rows('eventLog', [
      { field: 'maxEvents', label: 'Matches per query', help: 'The viewer says when a log holds more than this.', unit: 'events' },
      { field: 'maxScan', label: 'Records scanned for a text search', help: 'Never set below the match limit, or a search can never fill a page. Raising it makes rare-term searches work harder on the device.', unit: 'records' },
      { field: 'maxMessageChars', label: 'Maximum message length', help: 'Long messages are cut at the device.', unit: 'characters' },
      { field: 'queryTimeoutSeconds', label: 'Query timeout', help: 'How long the server waits for the device\'s answer.', unit: 'seconds' }
    ])
  },
  {
    title: 'Live performance monitoring',
    tab: 'devices',
    description:
      'The agent samples continuously and keeps a rolling buffer in memory, so the graphs are populated the moment a device page opens. Faster sampling costs the device more.',
    rows: rows('liveMetrics', [
      { field: 'historyWindowMinutes', label: 'Rolling history kept', help: 'How much live history the agent buffers and the graphs display.', unit: 'minutes' },
      { field: 'idleSampleSeconds', label: 'Sampling interval - idle', help: 'Cadence while nobody is watching the device.', unit: 'seconds' },
      { field: 'watchedSampleSeconds', label: 'Sampling interval - watched', help: 'Cadence while a technician has the device open.', unit: 'seconds' },
      { field: 'maintenanceSampleSeconds', label: 'Sampling interval - maintenance mode', help: 'Cadence for the samples the server persists.', unit: 'seconds' },
      { field: 'maintenanceWindowHours', label: 'Maintenance mode duration', help: 'How long maintenance mode runs, and how much of it is retained.', unit: 'hours' },
      { field: 'monitorWatchedPollSeconds', label: 'SNMP poll interval - watched', help: 'Cadence for a monitored SNMP device while its page is open. It never polls faster than the monitor\'s own interval - this only removes the slack in a slow one.', unit: 'seconds' }
    ])
  },
  {
    title: 'Device history',
    tab: 'devices',
    description:
      'The CRM\'s own record of sign-ins, reboots, software changes and staff actions. Lowering the retention deletes history: the prune runs on each device\'s next entry.',
    rows: rows('deviceHistory', [
      { field: 'maxStoredPerDevice', label: 'Entries kept per device', help: 'Oldest entries are pruned once a device exceeds this.', unit: 'entries' },
      { field: 'retentionDays', label: 'Retention', help: 'Nothing older than this is kept, however few entries a device has.', unit: 'days' },
      { field: 'defaultPageSize', label: 'Entries per page', help: 'What the History tab loads at a time.', unit: 'rows' },
      { field: 'maxPageSize', label: 'Maximum entries per page', help: 'Ceiling on a single page request.', unit: 'rows' },
      { field: 'maxEventsPerReport', label: 'Events per agent report', help: 'The rest wait for the device\'s next scan.', unit: 'events' },
      { field: 'maxDiffEvents', label: 'Software-change entries per inventory', help: 'A re-imaged machine is summarized past this rather than burying the timeline.', unit: 'entries' },
      { field: 'maxSummaryChars', label: 'Maximum summary length', help: '', unit: 'characters' },
      { field: 'maxDetailChars', label: 'Maximum detail length', help: '', unit: 'characters' },
      { field: 'maxActorChars', label: 'Maximum actor length', help: '', unit: 'characters' }
    ])
  },
  {
    title: 'Agent deployment',
    tab: 'devices',
    description:
      'Bounds on what a machine the CRM does not yet manage may report while an agent is being pushed onto it.',
    rows: rows('deploy', [
      { field: 'reportGraceMinutes', label: 'Report grace period', help: 'How long after its enrollment token expires a deployment may still report its outcome. A successful install spends the token on its way to reporting success, so this cannot be zero.', unit: 'minutes' },
      { field: 'maxMessageLength', label: 'Maximum message length', help: '', unit: 'characters' },
      { field: 'maxStageLength', label: 'Maximum stage-name length', help: '', unit: 'characters' }
    ])
  },
  {
    title: 'Knowledge base images',
    tab: 'files',
    description:
      'Images pasted into articles. They are stored on disk beside the database, not inside it, and de-duplicated by content.',
    rows: rows('kbMedia', [
      { field: 'maxBytes', label: 'Maximum image size', help: '', unit: 'MB', scale: MB },
      { field: 'maxPerArticle', label: 'Images per article', help: '', unit: 'images' },
      { field: 'maxFileNameChars', label: 'Maximum file-name length', help: '', unit: 'characters' }
    ])
  },
  {
    title: 'Synology monitoring',
    tab: 'devices',
    description:
      'A DSM/SRM login is expensive and lands in the customer\'s own connection log, which is why the inventory poll is far slower than the metric poll.',
    rows: rows('synology', [
      { field: 'defaultInventorySeconds', label: 'Inventory interval', help: 'Default for a newly enrolled device.', unit: 'seconds' },
      { field: 'minInventorySeconds', label: 'Minimum inventory interval', help: 'The floor an enrollment may not go below.', unit: 'seconds' },
      { field: 'apiTimeoutSeconds', label: 'Web API timeout', help: 'How long the agent waits on one DSM/SRM request.', unit: 'seconds' },
      { field: 'testTimeoutSeconds', label: 'Connection-test timeout', help: 'How long the server waits for an agent to answer a test.', unit: 'seconds' },
      { field: 'maxVolumes', label: 'Maximum volumes stored', help: '', unit: 'volumes' },
      { field: 'maxDisks', label: 'Maximum disks stored', help: '', unit: 'disks' },
      { field: 'maxTextLength', label: 'Maximum text field length', help: '', unit: 'characters' }
    ])
  },
  {
    title: 'Mail',
    tab: 'mail',
    description:
      'Bounds on an emailed invoice, and how hard the mailbox is polled for replies. The mail account itself - servers, sign-in, templates - is the card above.',
    rows: rows('mail', [
      { field: 'maxRecipients', label: 'Maximum recipients', help: 'To and Cc together, on one invoice.', unit: 'addresses' },
      { field: 'maxSubjectChars', label: 'Maximum subject length', help: '', unit: 'characters' },
      { field: 'maxBodyChars', label: 'Maximum message length', help: '', unit: 'characters' },
      { field: 'sendTimeoutSeconds', label: 'Send timeout', help: 'How long to wait on the mail server for one message.', unit: 'seconds' },
      { field: 'pollSeconds', label: 'Mailbox poll interval', help: 'POP3 has no push, so every check is a full connect-and-sign-in. One minute is the floor.', unit: 'seconds' },
      { field: 'maxMessagesPerPoll', label: 'Messages read per poll', help: 'A long-neglected mailbox is drained over several polls rather than in one pass.', unit: 'messages' },
      { field: 'maxInboundBodyChars', label: 'Maximum received-message length', help: '', unit: 'characters' },
      { field: 'inboundRetentionDays', label: 'Received-mail retention', help: '', unit: 'days' }
    ])
  }
];

export function rowKey(row: SettingRowSpec): string {
  return `${row.section}.${row.field}`;
}

/** The stored value of a row's member, or `null` where the section is absent. */
export function storedValue(settings: AppSettingsDto | null | undefined, row: SettingRowSpec): number | null {
  const section = settings?.[row.section] as Record<string, number> | null | undefined;
  const value = section?.[row.field];
  return typeof value === 'number' ? value : null;
}

/** What the box shows for a stored value: scaled, and the default in place of an unlimited 0. */
export function displayText(row: SettingRowSpec, stored: number, fallback: number): string {
  const scale = row.scale ?? 1;
  const source = stored <= 0 && row.allowUnlimited ? fallback : stored;
  return String(Math.floor(source / scale));
}

/** A whole non-negative number, or null for anything else - including a half-typed "12a". */
export function parseWhole(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) ? n : null;
}

/** The value to store: 0 when unlimited, otherwise the box scaled back up. Null when the box is not a number. */
export function toStored(row: SettingRowSpec, text: string, unlimited: boolean): number | null {
  if (unlimited && row.allowUnlimited) return 0;
  const n = parseWhole(text);
  return n === null ? null : n * (row.scale ?? 1);
}

export function defaultHint(row: SettingRowSpec, stored: number): string {
  const shown = `Default ${displayText(row, stored, stored)} ${row.unit}`;
  return row.allowUnlimited ? `${shown} · tick Unlimited to remove the limit` : shown;
}

/**
 * Every section filled in: a section the server sent as null takes the defaults' copy, the same reading
 * `AppSettings.Sanitize` gives it, so a value is never written into a section that does not exist.
 */
export function filled(settings: AppSettingsDto, defaults: AppSettingsDto): AppSettingsDto {
  const out = {} as Record<string, unknown>;
  for (const key of Object.keys(defaults) as SectionKey[]) {
    out[key] = { ...(defaults[key] ?? {}), ...(settings[key] ?? {}) };
  }
  return out as unknown as AppSettingsDto;
}

/** The document to save: what was loaded, with each row's value written back through its own member. */
export function buildSettings(base: AppSettingsDto, values: ReadonlyMap<string, number>): AppSettingsDto {
  const out = JSON.parse(JSON.stringify(base)) as Record<string, Record<string, number> | null>;
  for (const section of SETTING_SECTIONS) {
    for (const row of section.rows) {
      const value = values.get(rowKey(row));
      if (value === undefined) continue;
      const target = (out[row.section] ??= {});
      target[row.field] = value;
    }
  }
  return out as unknown as AppSettingsDto;
}
